import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao } from '../componentes/Campo.tsx';
import {
  Aviso,
  CabecalhoDePagina,
  CabecalhoDeTabela,
  Cartao,
  CLASSE_TABELA,
  Indicador,
} from '../componentes/Superficie.tsx';

type ContratoDaPrevia = {
  produto: string;
  operadora: string | null;
  titulares: string[];
  vidas: number | null;
  diaVencimento: number | null;
  implantacao: string;
  dataRenovacao: string;
  dataRevisao: string;
};

type ClienteDaPrevia = {
  linhas: number[];
  situacao: 'VALIDA' | 'ERRO' | 'DUPLICADA' | 'IGNORADA';
  nome: string | null;
  documento: string | null;
  contratos: ContratoDaPrevia[];
  avisos: string[];
  erro: string | null;
  contatoExistente: { id: string; nome: string } | null;
};

type Previa = {
  importacaoId: string;
  arquivoNome: string;
  colunasIgnoradas: string[];
  linhasLidas: number;
  clientes: number;
  contratos: number;
  duplicados: number;
  comErro: number;
  ignorados: number;
  itens: ClienteDaPrevia[];
};

type Resultado = {
  importacaoId: string;
  clientes: number;
  contratos: number;
  titulares: number;
  duplicados: number;
  comErro: number;
  ignorados: number;
};

type Historico = {
  id: string;
  tipo: 'LEADS' | 'CARTEIRA';
  arquivoNome: string;
  status: string;
  linhasLidas: number;
  importados: number;
  duplicados: number;
  comErro: number;
  criadaEm: string;
  usuaria: { nome: string };
};

type Usuaria = { id: string; nome: string; ativa: boolean };

const ROTULO_SITUACAO: Record<ClienteDaPrevia['situacao'], string> = {
  VALIDA: 'Pronto',
  ERRO: 'Erro',
  DUPLICADA: 'Já existe',
  IGNORADA: 'Ignorado',
};

const COR_SITUACAO: Record<ClienteDaPrevia['situacao'], string> = {
  VALIDA: 'text-sucesso',
  ERRO: 'text-erro',
  DUPLICADA: 'text-alerta',
  IGNORADA: 'text-texto-suave',
};

/**
 * A prévia fala em dias civis (AAAA-MM-DD). Passar por `Date` levaria a data
 * para a meia-noite UTC, e o fuso de Brasília a mostraria um dia antes.
 */
function diaCivil(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/**
 * Importar carteira.
 *
 * Traz os clientes que a Reducee já tinha antes do Sales Flow. Cada linha vira
 * cliente, Contrato herdado, titular e Renovação — sem funil, sem Implantação
 * e sem entrar em produção de ninguém (ADR 0015). Nenhum Contrato se exclui
 * depois, então a prévia mostra por cliente o que vai ser gravado.
 */
export function PaginaImportarCarteira(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const { usuaria } = useSessao();
  const campoArquivo = useRef<HTMLInputElement>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [responsavelId, setResponsavelId] = useState<string>('');

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Usuaria[]>('/usuarias'),
  });
  const ativas = (usuarias.data ?? []).filter((pessoa) => pessoa.ativa);
  const responsavel = responsavelId || usuaria?.id || '';

  const historico = useQuery({
    queryKey: ['importacoes'],
    queryFn: () => chamarApi<Historico[]>('/importacoes'),
    select: (todas) => todas.filter((importacao) => importacao.tipo === 'CARTEIRA'),
  });

  const tratarErro = (causa: unknown): void => {
    setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível ler o arquivo.');
  };

  const enviar = useMutation({
    mutationFn: async (campos: FormData) => {
      // Sem content-type nosso: o navegador monta o boundary do multipart.
      const resposta = await fetch('/api/importacoes/carteira/previa', {
        method: 'POST',
        credentials: 'same-origin',
        body: campos,
      });
      const dados = (await resposta.json().catch(() => null)) as Record<string, unknown> | null;
      if (!resposta.ok) {
        throw new ErroDaApi(
          resposta.status,
          typeof dados?.['mensagem'] === 'string' ? dados['mensagem'] : 'Falha ao ler o arquivo.',
          dados,
        );
      }
      return dados as unknown as Previa;
    },
    onSuccess: async (dados) => {
      setPrevia(dados);
      setResultado(null);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['importacoes'] });
    },
    onError: tratarErro,
  });

  const confirmar = useMutation({
    mutationFn: (id: string) =>
      chamarApi<Resultado>(`/importacoes/carteira/${id}/confirmar`, {
        metodo: 'POST',
        corpo: { responsavelId: responsavel },
      }),
    onSuccess: async (dados) => {
      setResultado(dados);
      setPrevia(null);
      setErro(null);
      if (campoArquivo.current) campoArquivo.current.value = '';
      await clienteDeConsultas.invalidateQueries({ queryKey: ['importacoes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['carteira'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['renovacoes'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
    },
    onError: tratarErro,
  });

  function aoLerArquivo(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const arquivo = campoArquivo.current?.files?.[0];
    if (!arquivo) {
      setErro('Escolha um arquivo antes de validar.');
      return;
    }
    setErro(null);
    const campos = new FormData();
    campos.set('arquivo', arquivo);
    enviar.mutate(campos);
  }

  const contagens = {
    linhasLidas: previa?.linhasLidas ?? 0,
    clientes: previa?.clientes ?? resultado?.clientes ?? 0,
    contratos: previa?.contratos ?? resultado?.contratos ?? 0,
    comErro: previa?.comErro ?? resultado?.comErro ?? 0,
    ignorados: previa?.ignorados ?? resultado?.ignorados ?? 0,
  };

  return (
    <section>
      <CabecalhoDePagina
        area="Clientes"
        titulo="Importar carteira"
        descricao="Traga os clientes que já têm plano: cada um entra com contrato, titular e renovação."
        acoes={
          <Link
            to="/clientes"
            className="rounded-[10px] border border-linha bg-superficie px-3.25 py-2.5 text-base font-[750] transition hover:bg-marinho-suave"
          >
            Voltar à carteira
          </Link>
        }
      />

      <Aviso tom="sucesso">
        <b>Contrato herdado não é venda nova:</b> fica fora do funil, da produção, da bonificação e
        das comissões. Entra para acompanhar a renovação, com o valor mensal em branco — ele se
        preenche depois, na Visão 360º do cliente.
      </Aviso>

      {erro ? (
        <p role="alert" className="mb-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao
          titulo="1. Selecionar arquivo"
          descricao="A planilha da carteira, com uma linha por titular."
        >
          <form onSubmit={aoLerArquivo}>
            <div className="grid gap-3">
              <div>
                <label
                  htmlFor="campo-planilha-carteira"
                  className="text-[11px] font-extrabold text-[#536575]"
                >
                  Arquivo Excel ou CSV
                </label>
                <input
                  id="campo-planilha-carteira"
                  ref={campoArquivo}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  required
                  className="mt-1.25 w-full rounded-[9px] border border-[#d7e0e6] bg-superficie px-2.75 py-2.5 text-sm"
                />
              </div>

              <CampoSelecao
                rotulo="Responsável pelos clientes"
                name="responsavelId"
                value={responsavel}
                onChange={(evento) => setResponsavelId(evento.target.value)}
                dica="Fica com os contratos e as renovações de toda a planilha."
              >
                {ativas.map((pessoa) => (
                  <option key={pessoa.id} value={pessoa.id}>
                    {pessoa.nome}
                  </option>
                ))}
              </CampoSelecao>
            </div>

            <div className="mt-4">
              <button
                type="submit"
                disabled={enviar.isPending}
                className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
              >
                {enviar.isPending ? 'Lendo o arquivo…' : 'Ler e validar arquivo'}
              </button>
            </div>
          </form>
        </Cartao>

        <Cartao titulo="Como a planilha é lida" descricao="Nada é gravado antes da confirmação.">
          <dl className="divide-y divide-linha">
            <Regra termo="Mesmo CPF ou CNPJ em várias linhas" valor="Um cliente só" />
            <Regra termo="Coluna Saúde, Odonto ou Seguro de vida" valor="Um contrato cada" />
            <Regra termo="Mesma operadora e implantação" valor="Mais um titular" />
            <Regra termo="Operadora" valor="Precisa estar cadastrada" />
            <Regra termo="Status cancelado" valor="Ignorado" />
            <Regra termo="Renovação" valor="Próximo aniversário da implantação" />
            <Regra termo="CNPJ · CPF" valor="PME · Individual" />
          </dl>
        </Cartao>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Indicador rotulo="Linhas lidas" valor={String(contagens.linhasLidas)} />
        <Indicador rotulo="Clientes prontos" valor={String(contagens.clientes)} />
        <Indicador rotulo="Contratos" valor={String(contagens.contratos)} />
        <Indicador rotulo="Com erro" valor={String(contagens.comErro)} />
        <Indicador rotulo="Ignorados" valor={String(contagens.ignorados)} />
      </div>

      {resultado ? (
        <div
          data-teste="resultado-importacao-carteira"
          className="mt-4 rounded-xl border border-sucesso/30 bg-sucesso/5 p-5"
        >
          <h2 className="text-base font-[850] text-marinho">Carteira importada</h2>
          <p className="mt-1 text-[13px]">
            {resultado.clientes} clientes, {resultado.contratos} contratos e {resultado.titulares}{' '}
            titulares entraram.
            {resultado.duplicados + resultado.comErro > 0
              ? ` ${resultado.duplicados + resultado.comErro} ficaram de fora na conferência final.`
              : ''}
          </p>
          <Link to="/renovacoes" className="mt-2 inline-block text-[13px] font-[750] text-marinho-claro underline">
            Ver as renovações
          </Link>
        </div>
      ) : null}

      <Cartao
        className="mt-4"
        titulo="Prévia da carteira"
        descricao="Confira cliente por cliente. Contrato importado não se exclui depois."
        acao={
          <button
            type="button"
            onClick={() => {
              if (previa) confirmar.mutate(previa.importacaoId);
            }}
            disabled={!previa || confirmar.isPending || previa.clientes === 0 || !responsavel}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
          >
            {confirmar.isPending
              ? 'Importando…'
              : previa && previa.clientes > 0
                ? `Importar ${previa.clientes} clientes`
                : 'Confirmar importação'}
          </button>
        }
      >
        {previa && previa.colunasIgnoradas.length > 0 ? (
          <p className="mb-3 text-[12px] text-texto-suave">
            Colunas ignoradas: {previa.colunasIgnoradas.join(', ')}
          </p>
        ) : null}

        <div className="overflow-x-auto">
          <table className={CLASSE_TABELA}>
            <CabecalhoDeTabela
              colunas={['Linha', 'Cliente', 'Contratos', 'Titulares', 'Vence', 'Renovação', 'Situação']}
            />
            <tbody data-teste={previa ? 'previa-carteira' : undefined}>
              {previa ? (
                previa.itens.map((item) => (
                  <tr
                    key={item.linhas.join('-')}
                    data-teste="cliente-da-previa"
                    className="border-b border-linha align-top last:border-0"
                  >
                    <td className="px-3.25 py-3 whitespace-nowrap">{item.linhas.join(', ')}</td>
                    <td className="px-3.25 py-3">
                      <span className="font-medium">{item.nome ?? '—'}</span>
                      {item.documento ? (
                        <span className="block text-xs text-texto-suave">{item.documento}</span>
                      ) : null}
                    </td>
                    <td className="px-3.25 py-3">
                      {item.contratos.length > 0
                        ? item.contratos.map((contrato, indice) => (
                            <span key={indice} className="block whitespace-nowrap">
                              {contrato.produto} · {contrato.operadora ?? 'sem operadora'}
                              {contrato.vidas ? (
                                <span className="text-xs text-texto-suave"> · {contrato.vidas} vida(s)</span>
                              ) : null}
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td className="px-3.25 py-3">
                      {item.contratos.length > 0
                        ? item.contratos.map((contrato, indice) => (
                            <span key={indice} className="block">
                              {contrato.titulares.length > 0 ? contrato.titulares.join(', ') : '—'}
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td className="px-3.25 py-3 whitespace-nowrap">
                      {item.contratos.length > 0
                        ? item.contratos.map((contrato, indice) => (
                            <span key={indice} className="block">
                              {contrato.diaVencimento ? `dia ${contrato.diaVencimento}` : '—'}
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td className="px-3.25 py-3 whitespace-nowrap">
                      {item.contratos.length > 0
                        ? item.contratos.map((contrato, indice) => (
                            <span key={indice} className="block">
                              {diaCivil(contrato.dataRenovacao)}
                              <span className="block text-xs text-texto-suave">
                                revisão {diaCivil(contrato.dataRevisao)}
                              </span>
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td className="px-3.25 py-3">
                      <span className={`font-medium ${COR_SITUACAO[item.situacao]}`}>
                        {ROTULO_SITUACAO[item.situacao]}
                      </span>
                      {item.erro ? (
                        <span className="block text-xs text-texto-suave">{item.erro}</span>
                      ) : null}
                      {item.avisos.map((aviso) => (
                        <span key={aviso} className="block text-xs text-alerta">
                          {aviso}
                        </span>
                      ))}
                      {item.contatoExistente ? (
                        <Link
                          to={`/contatos/${item.contatoExistente.id}`}
                          className="block text-xs text-marinho-claro underline"
                        >
                          Abrir {item.contatoExistente.nome}
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-4.5 text-[12px] text-texto-suave">
                    Selecione um arquivo para começar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Cartao>

      <Cartao className="mt-4" titulo="Últimas importações da carteira">
        {historico.data && historico.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={CLASSE_TABELA}>
              <CabecalhoDeTabela
                colunas={['Arquivo', 'Quando', 'Por', 'Linhas', 'Importadas', 'Erros', 'Situação']}
              />
              <tbody>
                {historico.data.map((importacao) => (
                  <tr key={importacao.id} className="border-b border-linha last:border-0">
                    <td className="px-3.25 py-3 font-medium">{importacao.arquivoNome}</td>
                    <td className="px-3.25 py-3">
                      {new Date(importacao.criadaEm).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3.25 py-3">{importacao.usuaria.nome}</td>
                    <td className="px-3.25 py-3">{importacao.linhasLidas}</td>
                    <td className="px-3.25 py-3">{importacao.importados}</td>
                    <td className="px-3.25 py-3">{importacao.comErro}</td>
                    <td className="px-3.25 py-3">
                      {importacao.status === 'CONFIRMADA' ? 'Importada' : 'Só prévia'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-4.5 text-[12px] text-texto-suave">Nenhuma carteira importada.</p>
        )}
      </Cartao>
    </section>
  );
}

/** Uma linha do quadro de regras: o que a planilha traz, e o que a importação faz. */
function Regra({ termo, valor }: { termo: string; valor: string }): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 py-2">
      <dt className="text-[12px] text-texto-suave">{termo}</dt>
      <dd className="text-[13px] font-[750] text-marinho">{valor}</dd>
    </div>
  );
}
